// @ts-check
// Caixa de conversas em duas colunas (lista de 360 px e painel), usada na página Conversas e,
// em layout compacto, na aba Conversas do card e da pessoa.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { listThreads } from '../../data/conversations.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { EmptyState } from '../../ui/feedback.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { currentWorkspaceMemberState } from '../../data/metadata-store.js';
import { DEFAULT_QUICK_FILTERS, ThreadList } from './thread-list.js';
import { ALL_ASSIGNEES, MY_CONVERSATIONS } from './conversation-assignee.js';
import { ConversationPane } from './conversation-pane.js';
import { CONVERSATION_POLLING_INTERVAL_MILLISECONDS, usePolling } from './conversations-state.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/conversations.js').ConversationThread} ConversationThread */

const THREAD_PAGE_LIMIT = 30;
const SEARCH_DEBOUNCE_MILLISECONDS = 300;
const FILTER_QUERIES = {
  all: {},
  whatsapp: { channelType: 'WHATSAPP' },
  email: { channelType: 'EMAIL' },
  unread: { unreadOnly: true },
};

/**
 * Filtro de responsável no formato da API: o membro logado no lugar de "Minhas conversas".
 * @param {string} assignee
 */
function toAssigneeQuery(assignee) {
  if (assignee === ALL_ASSIGNEES) return undefined;
  if (assignee === MY_CONVERSATIONS) return currentWorkspaceMemberState.value?.id ?? undefined;
  return assignee;
}

/**
 * Atualiza a primeira página sem perder as seguintes já carregadas, mais recentes primeiro.
 * @param {ConversationThread[]} current
 * @param {ConversationThread[]} firstPage
 */
function mergeThreads(current, firstPage) {
  const firstPageIds = new Set(firstPage.map((thread) => thread.id));
  const lastFirstPageTime = firstPage[firstPage.length - 1]?.lastMessage?.sentAt ?? '';
  const olderThreads = current.filter((thread) => !firstPageIds.has(thread.id) && (thread.lastMessage?.sentAt ?? '') < lastFirstPageTime);
  return [...firstPage, ...olderThreads];
}

/**
 * @param {{ selectedThreadId: string | null, onSelectThread: (threadId: string | null) => void, personId?: string | null,
 *   enrollmentOpportunityId?: string | null, householdId?: string | null, isCompact?: boolean, refreshKey?: number, emptyTitle?: string, emptySubtitle?: string,
 *   drilldown?: string | null, onClearDrilldown?: () => void, purpose?: 'CAPTURE' | 'SERVICE' | null }} props
 *   drilldown: caixa aberta por um clique num gráfico de conversas (`s.<receita>`), que restringe a lista às conversas do ponto
 *   purpose: finalidade das conversas da lista (3.0.0); a página Conversas pede CAPTURE com o módulo Atendimento ligado
 */
export function ConversationInbox({ selectedThreadId, onSelectThread, personId = null, enrollmentOpportunityId = null, householdId = null, isCompact = false, refreshKey = 0, emptyTitle, emptySubtitle,
  drilldown = null, onClearDrilldown, purpose = null }) {
  const [filter, setFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  // na aba do card e da pessoa a lista já vem filtrada e estreita; os filtros rápidos ficam só na página
  const [quickFilters, setQuickFilters] = useState(DEFAULT_QUICK_FILTERS);
  const activeQuickFilters = isCompact ? DEFAULT_QUICK_FILTERS : quickFilters;
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [threads, setThreads] = useState(/** @type {ConversationThread[] | null} */ (null));
  const [endCursor, setEndCursor] = useState(/** @type {string | null} */ (null));
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const requestCounterRef = useRef(0);

  const buildQuery = () => ({
    ...FILTER_QUERIES[/** @type {keyof typeof FILTER_QUERIES} */ (filter)],
    personId,
    enrollmentOpportunityId,
    householdId,
    searchInput: debouncedSearch,
    awaitingReply: activeQuickFilters.isAwaitingReplyOnly,
    withoutOpportunity: activeQuickFilters.isWithoutOpportunityOnly,
    assigneeWorkspaceMemberId: toAssigneeQuery(activeQuickFilters.assignee),
    limit: THREAD_PAGE_LIMIT,
    drilldown,
    purpose,
  });

  /** @param {{ isReset?: boolean }} [options] */
  const loadFirstPage = async ({ isReset = false } = {}) => {
    requestCounterRef.current += 1;
    const requestNumber = requestCounterRef.current;
    if (isReset) setThreads(null);
    try {
      const result = await listThreads(buildQuery());
      if (requestNumber !== requestCounterRef.current) return;
      setThreads((current) => (isReset || !current ? result.threads : mergeThreads(current, result.threads)));
      if (isReset) {
        setHasNextPage(Boolean(result.pageInfo.hasNextPage));
        setEndCursor(result.pageInfo.endCursor ?? null);
      }
    } catch (error) {
      if (requestNumber !== requestCounterRef.current) return;
      if (isReset) {
        setThreads([]);
        showErrorSnackbar(error);
      }
    }
  };

  useEffect(() => {
    loadFirstPage({ isReset: true });
  }, [filter, debouncedSearch, activeQuickFilters, personId, enrollmentOpportunityId, householdId, refreshKey, drilldown]);

  usePolling(() => loadFirstPage(), CONVERSATION_POLLING_INTERVAL_MILLISECONDS);

  const loadMore = async () => {
    if (!endCursor) return;
    setIsLoadingMore(true);
    try {
      const result = await listThreads({ ...buildQuery(), startingAfter: endCursor });
      setThreads((current) => {
        const existing = current ?? [];
        const ids = new Set(existing.map((thread) => thread.id));
        return [...existing, ...result.threads.filter((thread) => !ids.has(thread.id))];
      });
      setHasNextPage(Boolean(result.pageInfo.hasNextPage));
      setEndCursor(result.pageInfo.endCursor ?? null);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  /** @param {ConversationThread} updatedThread */
  const handleThreadChange = (updatedThread) => {
    setThreads((current) => {
      if (!current) return current;
      const exists = current.some((thread) => thread.id === updatedThread.id);
      if (!exists) return current;
      return current.map((thread) => (thread.id === updatedThread.id ? { ...thread, ...updatedThread } : thread));
    });
  };

  return html`<div class="conversation-inbox" data-compact=${isCompact ? 'true' : undefined} data-has-selection=${selectedThreadId ? 'true' : undefined}>
    <${ThreadList}
      threads=${threads}
      selectedThreadId=${selectedThreadId}
      onSelect=${(thread) => onSelectThread(thread.id)}
      filter=${filter}
      onFilterChange=${setFilter}
      searchInput=${searchInput}
      onSearchChange=${setSearchInput}
      quickFilters=${activeQuickFilters}
      onQuickFiltersChange=${setQuickFilters}
      showQuickFilters=${!isCompact}
      hasNextPage=${hasNextPage}
      onLoadMore=${loadMore}
      isLoadingMore=${isLoadingMore}
      emptyTitle=${emptyTitle}
      emptySubtitle=${emptySubtitle}
      drilldown=${drilldown}
      onClearDrilldown=${onClearDrilldown}
    />
    <section class="conversation-inbox-pane">
      ${selectedThreadId
        ? html`<${ConversationPane} key=${selectedThreadId} threadId=${selectedThreadId} onThreadChange=${handleThreadChange} onBack=${() => onSelectThread(null)} />`
        : html`<div class="conversation-pane conversation-pane-empty">
            <${EmptyState} icon="IconMessages" title=${t('conversations.noSelectionTitle')} subtitle=${t('conversations.noSelectionSubtitle')} />
          </div>`}
    </section>
  </div>`;
}
